import { useMemo } from "react";
import { cn } from "@/utils/Functions";
import { Card, CardHeader, CardTitle, CardPanel } from "@/components/ui/card";
import type { Deal } from "@/types/deal.types";
import type { Transaction } from "@/types/portfolio.types";
import { groupByCategory } from "./financials-utils";

const EXPENSE_COLORS: Record<string, string> = {
  talent: "bg-violet-500",
  production: "bg-blue-500",
  studio: "bg-cyan-500",
  marketing: "bg-pink-500",
  legal: "bg-amber-500",
  investment: "bg-emerald-500",
  other: "bg-slate-500",
};

type Props = {
  transactions: Transaction[];
  deals: Deal[];
};

export function RevenueExpenseBreakdown({ transactions, deals }: Props) {
  // Revenue by Deal — earned amount vs total deal value
  const revenueByDeal = useMemo(() => {
    const earnedMap: Record<string, number> = {};
    transactions
      .filter((t) => t.direction === "in" && t.dealId)
      .forEach((t) => {
        earnedMap[t.dealId!] = (earnedMap[t.dealId!] ?? 0) + t.amount;
      });

    return deals
      .filter((d) => earnedMap[d.id])
      .map((d) => ({
        dealId: d.id,
        name: d.name,
        earned: earnedMap[d.id],
        total: d.value,
        pct: d.value > 0 ? Math.round((earnedMap[d.id] / d.value) * 100) : 0,
      }))
      .sort((a, b) => b.earned - a.earned);
  }, [transactions, deals]);

  const expenseGroups = useMemo(() => groupByCategory(transactions, "out"), [transactions]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Revenue by Deal */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Revenue by Deal</CardTitle>
        </CardHeader>
        <CardPanel>
        {revenueByDeal.length > 0 ? (
          <div className="space-y-3">
            {revenueByDeal.map((deal) => (
              <div key={deal.dealId}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground truncate mr-2">{deal.name}</span>
                  <span className="text-xs font-medium text-foreground tabular-nums shrink-0">
                    ${deal.earned.toLocaleString()} / ${deal.total.toLocaleString()} ({deal.pct}%)
                  </span>
                </div>
                <div className="h-2 rounded-full bg-muted/50 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${Math.min(deal.pct, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-6">No revenue data</p>
        )}
        </CardPanel>
      </Card>

      {/* Expenses by Category */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Expenses by Category</CardTitle>
        </CardHeader>
        <CardPanel>
        {expenseGroups.length > 0 ? (
          <div className="space-y-3">
            {expenseGroups.map((group) => (
              <div key={group.category} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-24 shrink-0 truncate">{group.label}</span>
                <div className="flex-1 h-2 rounded-full bg-muted/50 overflow-hidden">
                  <div
                    className={cn("h-full rounded-full transition-all", EXPENSE_COLORS[group.category] ?? "bg-slate-500")}
                    style={{ width: `${group.pct}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-foreground tabular-nums w-20 text-right">
                  ${group.amount.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-6">No expense data</p>
        )}
        </CardPanel>
      </Card>
    </div>
  );
}
